import { useCallback, useEffect, useRef, useState } from "react"

import { getDouyinVideoPlayUrl } from "~/api/douyin"
import { uploadFile } from "~/api/upload"
import {
  fetchRemoteResourceFile,
  settleUploads,
  type UploadLocalResourceFailure,
} from "~/common/media/resourceUpload"
import {
  getLocalVideoDuration,
  getVideoDurationLimitError,
  validateLocalUploadFile,
  type UploadedResource,
} from "~/common/media/uploadedResources"
import { formatFileSize } from "~/common/utils"

export type UploadedVideoResource = UploadedResource & {
  duration: number
}

type VideoUploadOptions = {
  enforceDurationLimit?: boolean
  maxFileSize?: number
  videoTargetSizeMegabytes?: number
}

type VideoUploadStage =
  | "idle"
  | "resolving"
  | "fetching"
  | "reading"
  | "compressing"
  | "uploading"

type VideoUploadResult = {
  failures: UploadLocalResourceFailure[]
  resources: UploadedVideoResource[]
}

const getUploadError = (error: unknown) => {
  return error instanceof Error ? error : new Error("上传失败，请稍后重试")
}

const throwIfAborted = (signal: AbortSignal) => {
  if (signal.aborted) {
    throw new DOMException("上传已取消", "AbortError")
  }
}

const getFileSizeError = (maxFileSize: number) =>
  `视频大小不能超过 ${formatFileSize(maxFileSize)}`

const getVideoDuration = async (file: File, enforceDurationLimit: boolean) => {
  let duration: number

  try {
    duration = await getLocalVideoDuration(file)
  } catch (error) {
    throw new Error("读取视频时长失败，请重新选择视频", { cause: error })
  }

  const durationError = enforceDurationLimit
    ? getVideoDurationLimitError(duration)
    : ""

  if (durationError) throw new Error(durationError)

  return duration
}

const uploadVideoFile = async ({
  file,
  maxFileSize,
  videoTargetSizeMegabytes,
  onProgress,
  onStageChange,
  signal,
  enforceDurationLimit = true,
}: VideoUploadOptions & {
  file: File
  onProgress: (progress: number) => void
  onStageChange: (stage: VideoUploadStage) => void
  signal: AbortSignal
}) => {
  throwIfAborted(signal)
  const validation = validateLocalUploadFile(file, "video")

  if (!validation.valid) throw new Error(validation.message)
  if (maxFileSize && file.size > maxFileSize) {
    throw new Error(getFileSizeError(maxFileSize))
  }

  onStageChange("reading")
  const duration = await getVideoDuration(file, enforceDurationLimit)
  throwIfAborted(signal)
  onStageChange("uploading")

  const result = await uploadFile<UploadedResource>({
    file,
    onProgress: (progress) => {
      onStageChange("uploading")
      onProgress(progress)
    },
    onVideoCompressionProgress: (progress) => {
      if (progress === null) return

      onStageChange("compressing")
      onProgress(Math.round(progress * 100))
    },
    signal,
    type: "video",
    videoTargetSizeMegabytes,
  })

  return { ...result.data, duration }
}

const uploadVideoFiles = async ({
  files,
  onProgress,
  onStageChange,
  options,
  signal,
}: {
  files: File[]
  onProgress: (progress: number) => void
  onStageChange: (stage: VideoUploadStage) => void
  options: VideoUploadOptions
  signal: AbortSignal
}): Promise<VideoUploadResult> => {
  return settleUploads(
    files.map((file) => ({ file, kind: "video" as const })),
    ({ file }) =>
      uploadVideoFile({
        ...options,
        file,
        onProgress,
        onStageChange,
        signal,
      }),
  )
}

const getStatus = (stage: VideoUploadStage, progress: number) => {
  switch (stage) {
    case "resolving":
      return "解析视频链接..."
    case "fetching":
      return "获取视频文件..."
    case "reading":
      return "读取视频信息..."
    case "compressing":
      return `压缩视频中 ${progress}%`
    case "uploading":
      return progress > 0 ? `上传视频中 ${progress}%` : "上传视频中..."
    default:
      return ""
  }
}

export const useVideoUpload = () => {
  const controllerRef = useRef<AbortController | undefined>(undefined)
  const mountedRef = useRef(true)
  const [progress, setProgress] = useState(0)
  const [stage, setStage] = useState<VideoUploadStage>("idle")

  const cancel = useCallback(() => {
    controllerRef.current?.abort()
    controllerRef.current = undefined

    if (mountedRef.current) {
      setProgress(0)
      setStage("idle")
    }
  }, [])

  useEffect(() => {
    mountedRef.current = true

    return () => {
      mountedRef.current = false
      controllerRef.current?.abort()
    }
  }, [])

  const run = useCallback(
    async <T>(
      initialStage: VideoUploadStage,
      action: (
        signal: AbortSignal,
        onStageChange: (nextStage: VideoUploadStage) => void,
        onProgress: (nextProgress: number) => void,
      ) => Promise<T>,
    ) => {
      controllerRef.current?.abort()
      const controller = new AbortController()
      controllerRef.current = controller

      if (mountedRef.current) {
        setProgress(0)
        setStage(initialStage)
      }

      try {
        return await action(
          controller.signal,
          (nextStage) => {
            if (mountedRef.current && controllerRef.current === controller) {
              setStage(nextStage)
            }
          },
          (nextProgress) => {
            if (mountedRef.current && controllerRef.current === controller) {
              setProgress(nextProgress)
            }
          },
        )
      } finally {
        if (controllerRef.current === controller) {
          controllerRef.current = undefined

          if (mountedRef.current) {
            setProgress(0)
            setStage("idle")
          }
        }
      }
    },
    [],
  )

  const uploadVideos = useCallback(
    (files: File[], options: VideoUploadOptions = {}) => {
      return run("reading", (signal, onStageChange, onProgress) =>
        uploadVideoFiles({
          files,
          onProgress,
          onStageChange,
          options,
          signal,
        }),
      )
    },
    [run],
  )

  const uploadVideo = useCallback(
    async (file: File, options: VideoUploadOptions = {}) => {
      const result = await uploadVideos([file], options)
      const failure = result.failures[0]

      if (failure) throw getUploadError(failure.error)

      const resource = result.resources[0]

      if (!resource) throw new Error("上传失败，请稍后重试")

      return resource
    },
    [uploadVideos],
  )

  const uploadDouyinVideo = useCallback(
    (rawUrl: string, options: VideoUploadOptions = {}) => {
      return run("resolving", async (signal, onStageChange, onProgress) => {
        const videoUrl = rawUrl.trim()

        if (!videoUrl) throw new Error("请输入抖音视频链接")

        const response = await getDouyinVideoPlayUrl(
          { video_url: videoUrl },
          signal,
        )
        const playUrl = response.data?.play_url

        if (!playUrl) throw new Error("未获取到视频播放地址")

        onStageChange("fetching")
        const file = await fetchRemoteResourceFile({
          fallbackName: "抖音视频",
          kind: "video",
          signal,
          url: playUrl,
        })
        const result = await uploadVideoFiles({
          files: [file],
          onProgress,
          onStageChange,
          options,
          signal,
        })
        const failure = result.failures[0]

        if (failure) throw getUploadError(failure.error)

        const resource = result.resources[0]

        if (!resource) throw new Error("上传失败，请稍后重试")

        return resource
      })
    },
    [run],
  )

  return {
    cancel,
    isBusy: stage !== "idle",
    progress,
    stage,
    status: getStatus(stage, progress),
    uploadDouyinVideo,
    uploadVideo,
    uploadVideos,
  }
}
